import type { PostingCadence, SocialPlatform } from '../../../../api/venues';
import { VerifiedAge } from '../../primitives/VerifiedAge';
import { Field, fieldStyle } from '../venueCreateFields';

import { Input } from './Input';
import { PrefixInput } from './PrefixInput';
import {
  CADENCE_CHOICES,
  PLATFORM_LABELS,
  socialErrorKey,
  type PlatformDraft,
} from './social-form';
import { normalizeHandle } from './social-format';

const HANDLE_PREFIX: Record<SocialPlatform, string> = {
  instagram: '@',
  facebook: 'facebook.com/',
  tiktok: '@',
};

interface SocialPlatformFormProps {
  platform: SocialPlatform;
  draft: PlatformDraft;
  onChange: (next: PlatformDraft) => void;
  /** Server and form errors, keyed by dotted path. */
  errors: Record<string, string>;
  verifiedAt: string | undefined;
  /** Stack the three fields on a narrow screen. */
  compact: boolean;
}

/** Handle, followers and cadence side by side on a wide screen. */
const FIELD_COLUMNS = 'minmax(0,1.4fr) 110px 150px';

/** The edit fields for one platform: handle, audience and posting cadence. */
export function SocialPlatformForm({
  platform,
  draft,
  onChange,
  errors,
  verifiedAt,
  compact,
}: SocialPlatformFormProps) {
  const handleError = errors[socialErrorKey(platform, 'handle')];
  const audienceError = errors[socialErrorKey(platform, 'audienceSize')];
  const cadenceError = errors[socialErrorKey(platform, 'cadence')];
  const label = PLATFORM_LABELS[platform];

  return (
    <div
      role="group"
      aria-label={label}
      style={{
        border: '1px solid var(--nb-border)',
        borderRadius: 10,
        padding: '12px 14px',
        background: 'var(--nb-surface)',
        minWidth: 0,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
        <span style={{ fontSize: 12.5, fontWeight: 700 }}>{label}</span>
        <span style={{ flex: 1 }} />
        <VerifiedAge at={verifiedAt} />
      </div>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: compact ? '1fr' : FIELD_COLUMNS,
          gap: 12,
        }}
      >
        <Field label={`${label} handle`} error={handleError}>
          <PrefixInput
            prefix={HANDLE_PREFIX[platform]}
            value={draft.handle}
            onChange={(v) => onChange({ ...draft, handle: normalizeHandle(v) })}
            placeholder="handle"
            error={handleError !== undefined}
          />
        </Field>
        <Field label={`${label} audience`} error={audienceError}>
          <Input
            mono
            value={draft.audience}
            onChange={(v) => onChange({ ...draft, audience: v })}
            placeholder="unknown"
            error={audienceError !== undefined}
          />
        </Field>
        <Field label={`${label} posting cadence`} error={cadenceError}>
          <select
            value={draft.cadence}
            onChange={(e) => onChange({ ...draft, cadence: e.target.value as PostingCadence })}
            style={fieldStyle(cadenceError ? 'error' : undefined)}
          >
            {CADENCE_CHOICES.map((c) => (
              <option key={c.value} value={c.value}>
                {c.label}
              </option>
            ))}
          </select>
        </Field>
      </div>
    </div>
  );
}
